import dtfIndexAbiV4 from '@/abis/dtf-index-abi-v4'
import useAssetPricesWithSnapshot, {
  TokenPriceWithSnapshot,
} from '@/hooks/use-asset-prices-with-snapshot'
import {
  indexDTFAtom,
  indexDTFBasketAtom,
  indexDTFRebalanceControlAtom,
  isHybridDTFAtom,
} from '@/state/dtf/atoms'
import { Token, Volatility } from '@/types'
import { calculatePriceFromRange } from '@/utils'
import {
  Rebalance,
  WeightRange,
} from '@reserve-protocol/dtf-rebalance-lib/dist/types'
import { useAtomValue } from 'jotai'
import { useMemo } from 'react'
import { useReadContract } from 'wagmi'
import { currentRebalanceAtom } from '../../../atoms'
import { originalRebalanceWeightsAtom, rebalanceAuctionsAtom } from '../atoms'
import useRebalanceCurrentData from './use-rebalance-current-data'
import useRebalanceInitialData from './use-rebalance-initial-data'
import useRebalancePriceVolatility from './use-rebalance-price-volatility'

export type RebalanceParams = {
  supply: bigint
  initialSupply: bigint
  rebalance: Rebalance
  currentAssets: Record<string, bigint>
  initialAssets: Record<string, bigint>
  initialPrices: Record<string, number>
  initialWeights: Record<string, WeightRange>
  prices: TokenPriceWithSnapshot
  tokenPriceVolatility: Record<string, Volatility>
  isTrackingDTF: boolean
}

const useRebalanceParams = () => {
  const dtf = useAtomValue(indexDTFAtom)
  const basket = useAtomValue(indexDTFBasketAtom)
  const rebalance = useAtomValue(currentRebalanceAtom)
  const rebalanceControl = useAtomValue(indexDTFRebalanceControlAtom)
  const isHybridDTF = useAtomValue(isHybridDTFAtom)
  const auctions = useAtomValue(rebalanceAuctionsAtom)
  const originalWeights = useAtomValue(originalRebalanceWeightsAtom)
  const tokenPriceVolatility = useRebalancePriceVolatility()

  const rebalanceTokens = useMemo(() => {
    if (!rebalance || !basket) return []

    const tokens = new Set<string>()

    rebalance.rebalance.tokens.forEach((token) => {
      tokens.add(token.address.toLowerCase())
    })

    basket.forEach((token) => {
      tokens.add(token.address.toLowerCase())
    })

    return Array.from(tokens)
  }, [basket, rebalance])

  const { data: prices } = useAssetPricesWithSnapshot(rebalanceTokens)
  const { data: currentRebalanceData } = useRebalanceCurrentData()
  const { data: initialRebalanceData } = useRebalanceInitialData()
  const { data: initialRebalance } = useReadContract({
    abi: dtfIndexAbiV4,
    address: dtf?.id,
    functionName: 'getRebalance',
    chainId: dtf?.chainId,
    args: [],
    blockNumber: BigInt(rebalance?.rebalance.blockNumber ?? '0'),
    query: {
      enabled: !!rebalance?.rebalance.blockNumber && !!dtf?.id,
    },
  })

  return useMemo(() => {
    if (
      !currentRebalanceData ||
      !initialRebalanceData ||
      !prices ||
      !rebalanceControl ||
      !rebalance ||
      !initialRebalance ||
      !tokenPriceVolatility
    )
      return undefined

    const { supply: initialSupply, initialAssets } = initialRebalanceData
    const tokenMap = rebalance.rebalance.tokens.reduce(
      (acc, token) => {
        acc[token.address.toLowerCase()] = token
        return acc
      },
      {} as Record<string, Token>
    )
    const initialPrices: Record<string, number> = {}
    let initialWeights: Record<string, WeightRange> = {}

    for (let i = 0; i < initialRebalance[1].length; i++) {
      const token = initialRebalance[1][i].toLowerCase()
      const decimals = tokenMap[token].decimals

      initialPrices[token] = calculatePriceFromRange(
        {
          low: initialRebalance[3][i].low,
          high: initialRebalance[3][i].high,
        },
        decimals
      )

      initialWeights[token] = {
        low: initialRebalance[2][i].low,
        spot: initialRebalance[2][i].spot,
        high: initialRebalance[2][i].high,
      }
    }

    // Use historical weights for subsequent auctions in hybrid DTFs
    if (isHybridDTF && auctions.length > 0 && originalWeights) {
      initialWeights = originalWeights
    }

    return {
      initialSupply: initialSupply,
      initialPrices,
      initialWeights,
      initialAssets,
      currentAssets: currentRebalanceData.currentAssets,
      supply: currentRebalanceData.supply,
      prices,
      tokenPriceVolatility,
      isTrackingDTF: !rebalanceControl.weightControl,
      rebalance: {
        nonce: currentRebalanceData.rebalance[0],
        tokens: currentRebalanceData.rebalance[1],
        weights: currentRebalanceData.rebalance[2],
        initialPrices: currentRebalanceData.rebalance[3],
        inRebalance: currentRebalanceData.rebalance[4],
        limits: currentRebalanceData.rebalance[5],
        startedAt: currentRebalanceData.rebalance[6],
        restrictedUntil: currentRebalanceData.rebalance[7],
        availableUntil: currentRebalanceData.rebalance[8],
        priceControl: currentRebalanceData.rebalance[9],
      } as Rebalance,
    } as RebalanceParams
  }, [
    currentRebalanceData,
    initialRebalanceData,
    initialRebalance,
    prices,
    rebalance,
    rebalanceControl,
    isHybridDTF,
    auctions.length,
    originalWeights,
    tokenPriceVolatility,
  ])
}

export default useRebalanceParams
